import React, { useState, useEffect, Suspense, lazy } from 'react';
import { getRealm, getBrandFavicon } from '/utils/host';
import { jwtDecode } from 'jwt-decode';
import env from '/env';
import MockLogin from '/components/mock_login';
import { removeAllCookies } from "/utils/string"
import configConstructor from "./configConstructor"
import { Loader } from "@idexcel-technologies-pvt-ltd/cync-los-common-components"
import { activteAccessibililty } from "./utils/accessibility"
import { checkAuthentication } from './services/auth';
/**
 * Don't change below code invoking order, if you have to modify make sure existing style not breaking.  
 */
import "./assets/css/default.css";
import "./assets/default/style.css";
import '/assets/css/app.scss';
import "/assets/css/app-media.scss";
import LoginRoutes from './utils/login_routes';

const AuthenticatedApp = lazy(() => import('./authenticated_app'));

const isDevEnv = env.APP_ENV === 'development';
const realm = isDevEnv ? (getRealm() || 'xyz') : getRealm();

const ensureLink = ({ id, rel, href, head }) => {
  let link = document.querySelector(`#${id}`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    link.setAttribute('id', id);
    head.appendChild(link);
  }
  if (link.getAttribute('href') !== href) {
    link.setAttribute('href', href);
  }
  return link;
};

const waitForStylesheet = (link) => {
  if (!link) return Promise.resolve();

  return new Promise((resolve) => {
    if (link.sheet) {
      resolve();
      return;
    }

    const finish = () => {
      link.removeEventListener('load', finish);
      link.removeEventListener('error', finish);
      resolve();
    };

    link.addEventListener('load', finish, { once: true });
    link.addEventListener('error', finish, { once: true });
    setTimeout(finish, 3000);
  });
};

const shouldUseDefaultBrand = async (realm) => {
  try {
    const { data } = await getBrandFavicon(realm);
    if (typeof data === 'string') {
      return data.includes('<!DOCTYPE html>') || data.includes('<!doctype html>');
    }
    return false;
  } catch {
    return true;
  }
};

const applyBrandAssets = async (realm) => {
  if (!realm) return;

  const head = document.querySelector('head');
  if (!head) return;

  const useDefaultBrand = await shouldUseDefaultBrand(realm);
  const brandPath = useDefaultBrand ? '/default' : `/brands/${realm}`;

  window.__LOS__logoRelativePath = `${brandPath}/logo.png`;

  const styleLink = ensureLink({
    id: `brand-${realm}-css`,
    rel: 'stylesheet',
    href: `${brandPath}/style.css`,
    head,
  });

  ensureLink({
    id: `brand-${realm}-favicon`,
    rel: 'icon',
    href: `${brandPath}/favicon.ico`,
    head,
  });

  const loaderLink = ensureLink({
    id: `brand-${realm}-loader-css`,
    rel: 'stylesheet',
    href: `${brandPath}/loading.css`,
    head,
  });

  await Promise.all([
    waitForStylesheet(styleLink),
    waitForStylesheet(loaderLink),
  ]);
};


const App = (() => {
  // Require __LOS__getEnv to be available for login page aswell
  window.__LOS__getEnv = configConstructor.getEnv;

  return () => {
    const [brandReady, setBrandReady] = useState(false);
    const [authState, setAuthState] = useState({
      loading: true,
      authenticated: false,
      token: null,
      auth: null
    });

    useEffect(() => {
      let isMounted = true;
      applyBrandAssets(realm).finally(() => {
        if (isMounted) {
          setBrandReady(true);
        }
      });

      return () => {
        isMounted = false;
      };
    }, []);

    useEffect(() => {
      checkAuthentication().then(authResult => {
        if (authResult.isAuthenticated && authResult[`${realm}_los_token`]) {
          let decodedAuth = null;
          try {
            decodedAuth = jwtDecode(authResult[`${realm}_los_token`]);
          } catch (error) {
            console.error('Failed to decode auth token:', error);
          }
          const isValidRealm = decodedAuth && decodedAuth.realm === realm;

          setAuthState({
            loading: false,
            authenticated: isValidRealm,
            token: authResult[`${realm}_los_token`],
            auth: decodedAuth
          });
        } else {
          setAuthState({
            loading: false,
            authenticated: false,
            token: null,
            auth: null
          });
        }
      }).catch(error => {
        console.error('Authentication check failed:', error);
        setAuthState({
          loading: false,
          authenticated: false,
          token: null,
          auth: null
        });
      });
    }, []);

    if (!brandReady) {
      return null;
    }

    if (authState.loading) {
      return <Loader />;
    }

      if (authState.authenticated) {
        return (
          <Suspense fallback={<Loader />}>
            <AuthenticatedApp auth={authState.auth} />
          </Suspense>
        );
      } else {
        return <UnauthenticatedApp />;
      }
    };
})();


const UnauthenticatedApp = () => {
  useEffect(() => {
    window.__LOS__getEnv = configConstructor.getEnv;

    const authRoutes = ['/login', '/logout', '/verify-account', '/forgot-password', '/reset-password', '/api', '/mcp/login'];
    const isAuthRoute = authRoutes.some(route => window.location.pathname.startsWith(route));

    if (!isAuthRoute) {
      sessionStorage.setItem('requestedURL', window.location.href);
      localStorage.setItem(`${realm}_isUserAuthenticated`, 'isUserAuthenticated');
    }

    activteAccessibililty(realm);
  }, []);

  if (!realm && !isDevEnv) {
    console.error('The realm could not be determined from', window.location.host);
    return <div>Invalid Application Request</div>;
  }

  if (!isDevEnv) {
    return <LoginRoutes />;
  } else {
    removeAllCookies();
    return <MockLogin subdomain={realm} />;
  }
};

// render(<App />, document.getElementById('app') || document.createElement('div'));
export default App;